/**
 * MODULE: _shared/frontend-shell/src/design-system/components/AiAssistant/AiAssistantMarkdown.tsx
 * PURPOSE: GFM markdown for assistant bubbles + syntax highlighting (rehype-highlight / lowlight).
 * RECENT: 2026-07-08 [2.2.1553] — `normalizeAssistantMarkdown` + compact mode; GFM list spacing before render.
 * RECENT_PRIOR: 2026-04-27 — **`rehype-highlight`**: `languages` must be a grammar map (`common`), not a Lowlight instance (fixes console errors from registering `highlight` / `register` as languages). 2026-04-26 — **Light/dark**: `pre` + copy chip + prose contrast; hljs theme injected by **`AiAssistantPanel`** (`github` vs `github-dark`). **Copy** on fenced `pre`.
 */

import { useRef, useState, type ReactNode } from 'react';
import { ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import type { Components } from 'react-markdown';
import ReactMarkdown from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import remarkGfm from 'remark-gfm';
import { normalizeAssistantMarkdown } from './assistantMarkdownNormalize';

function MarkdownPreWithCopy({ children }: { children?: ReactNode }) {
  const preRef = useRef<HTMLPreElement>(null);
  const [copied, setCopied] = useState(false);

  const onCopy = async () => {
    const el = preRef.current;
    if (!el) return;
    const text = el.innerText;
    if (!text.trim()) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="group/pre relative mb-2">
      <button
        type="button"
        onClick={() => void onCopy()}
        className="absolute right-2 top-2 z-10 rounded-md bg-white/95 px-2 py-1 text-[11px] font-medium text-gray-800 opacity-0 shadow-md ring-1 ring-gray-200/80 transition hover:bg-gray-50 group-hover/pre:opacity-100 focus:opacity-100 dark:bg-gray-800/95 dark:text-gray-100 dark:ring-white/10 dark:hover:bg-gray-800"
        aria-label="Copy code block"
      >
        {copied ? (
          'Copied'
        ) : (
          <span className="inline-flex items-center gap-1">
            <ClipboardDocumentIcon className="h-3.5 w-3.5" aria-hidden />
            Copy
          </span>
        )}
      </button>
      <pre
        ref={preRef}
        className="max-h-72 max-w-full overflow-auto rounded-lg border border-slate-200 bg-slate-100 p-3 pr-14 pt-3 text-[12px] text-slate-900 shadow-inner dark:border-gray-500/40 dark:bg-[#0d1117] dark:text-inherit"
      >
        {children}
      </pre>
    </div>
  );
}

const components: Components = {
  p: ({ children }) => (
    <p className="mb-2 text-[13px] leading-relaxed text-gray-800 last:mb-0 dark:text-gray-200">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="mb-2 list-disc space-y-0.5 pl-4 text-[13px] text-gray-800 dark:text-gray-200">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="mb-2 list-decimal space-y-0.5 pl-4 text-[13px] text-gray-800 dark:text-gray-200">{children}</ol>
  ),
  li: ({ children }) => (
    <li className="leading-relaxed text-gray-800 dark:text-gray-200">{children}</li>
  ),
  strong: ({ children }) => (
    <strong className="font-semibold text-gray-900 dark:text-gray-50">{children}</strong>
  ),
  a: ({ href, children }) => (
    <a
      href={href}
      className="font-medium text-primary-600 underline decoration-primary-600/40 underline-offset-2 hover:decoration-primary-600 dark:text-primary-400 dark:decoration-primary-400/40"
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  ),
  h1: ({ children }) => <h1 className="mb-2 text-base font-bold text-gray-900 dark:text-white">{children}</h1>,
  h2: ({ children }) => <h2 className="mb-2 text-sm font-bold text-gray-900 dark:text-white">{children}</h2>,
  h3: ({ children }) => <h3 className="mb-1 text-sm font-semibold text-gray-900 dark:text-white">{children}</h3>,
  blockquote: ({ children }) => (
    <blockquote className="mb-2 border-l-2 border-primary-400/60 pl-2 text-gray-700 italic dark:border-primary-500/50 dark:text-gray-300">
      {children}
    </blockquote>
  ),
  code: ({ className, children, ...props }) => {
    const block = Boolean(className?.includes('language-') || className?.includes('hljs'));
    if (!block) {
      return (
        <code className="rounded bg-gray-200/90 px-1 py-0.5 font-mono text-[12px] text-gray-900 dark:bg-gray-700/90 dark:text-gray-100">
          {children}
        </code>
      );
    }
    return (
      <code
        className={['hljs block font-mono text-[12px] leading-relaxed', className].filter(Boolean).join(' ')}
        {...props}
      >
        {children}
      </code>
    );
  },
  pre: MarkdownPreWithCopy,
  hr: () => <hr className="my-3 border-gray-300 dark:border-gray-600" />,
  table: ({ children }) => (
    <div className="mb-2 max-w-full overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-600">
      <table className="min-w-full border-collapse text-left text-[12px]">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-gray-100 dark:bg-gray-800">{children}</thead>,
  th: ({ children }) => (
    <th className="border-b border-gray-200 px-2 py-1.5 font-semibold text-gray-900 dark:border-gray-600 dark:text-gray-100">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border-b border-gray-100 px-2 py-1.5 text-gray-800 dark:border-gray-700 dark:text-gray-200">
      {children}
    </td>
  ),
};

export interface AiAssistantMarkdownProps {
  content: string;
  /** Tighter typography for table captions and rail snippets. */
  compact?: boolean;
}

export function AiAssistantMarkdown({ content, compact = false }: AiAssistantMarkdownProps) {
  const normalized = normalizeAssistantMarkdown(content);
  return (
    <div
      className={[
        'min-w-0 text-gray-900 dark:text-gray-100',
        compact ? 'text-xs leading-relaxed' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[[rehypeHighlight, { detect: true }]]}
        components={components}
      >
        {normalized}
      </ReactMarkdown>
    </div>
  );
}
